"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, Building2, Globe } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
interface CenterLocation {
  id: string;
  name: string;
  address: string;
  anchor: string;
}
const centersData: CenterLocation[] = [{
  id: "tan-mai",
  name: "ROBOTO Tân Mai",
  address: "Số 9 Louis II, KĐT Louis City Hoàng Mai, Hoàng Mai, Hà Nội",
  anchor: "#center-tan-mai"
}, {
  id: "xa-dan",
  name: "ROBOTO Xã Đàn",
  address: "199 Xã Đàn, Đống Đa, Hà Nội",
  anchor: "#center-xa-dan"
}, {
  id: "tam-trinh",
  name: "ROBOTO Tam Trinh",
  address: "1611 - 3-01 Khu Gelexia - Geleximco, Hoàng Mai, Hà Nội",
  anchor: "#center-tam-trinh"
}, {
  id: "hoang-quoc-viet",
  name: "ROBOTO Hoàng Quốc Việt",
  address: "136 Hoàng Quốc Việt, phường Nghĩa Đô, Hà Nội",
  anchor: "#center-hoang-quoc-viet"
}];
export default function CentersHero() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const getInitialLang = (): "vi" | "en" => {
      if (typeof window !== "undefined") {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") return stored;
      }
      return "vi";
    };
    setLang(getInitialLang());
    const handleLangChange = () => {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const toggleLanguage = (newLang: "vi" | "en") => {
    setLang(newLang);
    if (typeof window !== "undefined") {
      localStorage.setItem("roboto_lang", newLang);
      window.dispatchEvent(new CustomEvent("roboto-lang-change"));
    }
  };
  const copy = {
    vi: {
      badge: "Mạng lưới cơ sở",
      h1: "HỆ THỐNG CƠ SỞ ROBOTO",
      subtitle: "Khám phá các cơ sở ROBOTO và lựa chọn địa điểm học tập thuận tiện cho học viên.",
      btn: "Xem cơ sở",
      ariaLang: "Chuyển đổi ngôn ngữ"
    },
    en: {
      badge: "Center Network",
      h1: "ROBOTO Center Network",
      subtitle: "Explore ROBOTO's centers and choose the most convenient location for your child.",
      btn: "View center",
      ariaLang: "Language switcher"
    }
  };
  const currentCopy = copy[lang];
  return <section id="centers-hero" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border/80 overflow-hidden">
      {/* Engineering blueprint background grid pattern */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px] opacity-60" aria-hidden="true" />

      {/* Subtle brand glow decoration behind content */}
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[36rem] h-[18rem] bg-secondary/15 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Language Switcher Bar */}
        <div className="flex justify-end mb-6">
          <div className="inline-flex items-center gap-1.5 p-1 bg-muted/80 backdrop-blur-xs rounded-full border border-border text-xs font-medium">
            <Globe className="size-3.5 text-muted-foreground ml-1.5" aria-hidden="true" />
            <Button type="button" variant="ghost" onClick={() => toggleLanguage("vi")} className={`h-auto px-2.5 py-1 rounded-full transition-colors cursor-pointer ${lang === "vi" ? "bg-primary text-primary-foreground font-semibold shadow-xs hover:bg-primary hover:text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-transparent"}`} aria-label="Tiếng Việt">
              VIE
            </Button>
            <Button type="button" variant="ghost" onClick={() => toggleLanguage("en")} className={`h-auto px-2.5 py-1 rounded-full transition-colors cursor-pointer ${lang === "en" ? "bg-primary text-primary-foreground font-semibold shadow-xs hover:bg-primary hover:text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-transparent"}`} aria-label="English">
              ENG
            </Button>
          </div>
        </div>

        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }}>
            <Badge variant="outline" className="border-secondary/50 bg-secondary/10 text-foreground py-1 px-3 gap-1.5 mb-3">
              <Building2 className="size-3.5 text-secondary" />
              <span>{currentCopy.badge}</span>
            </Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-serif">
              {currentCopy.h1}
            </h1>
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.35,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            {currentCopy.subtitle}
          </motion.p>
        </div>

        {/* Center Technical Lab Showcase Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.35,
        delay: 0.15,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-8 max-w-5xl mx-auto rounded-2xl overflow-hidden border border-border shadow-md bg-card group">
          <div className="relative aspect-[21/9] sm:aspect-[24/9] w-full overflow-hidden bg-muted">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_lab_banner.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_lab_banner.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_lab_banner.png-wvc-sizes" alt="Không gian phòng lab robotics hiện đại tại hệ thống cơ sở ROBOTO" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
            {/* Engineered Technical Deep-Blue Vignette Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent flex items-end p-4 sm:p-6" aria-hidden="true">
              <div className="flex flex-wrap items-center justify-between w-full gap-2 text-white font-medium text-xs sm:text-sm">
                <span className="inline-flex items-center gap-2 bg-slate-900/90 backdrop-blur-md px-3.5 py-1.5 rounded-lg border border-white/15 shadow-sm text-white">
                  <span className="size-2 rounded-lg bg-secondary animate-pulse" />
                  <span>Tiêu Chuẩn Phòng Lab STEM & Robotics</span>
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Navigation Strip */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.35,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-10 sm:mt-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {centersData.map((center, index) => <motion.div key={center.id} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            delay: 0.2 + index * 0.06,
            ease: [0.16, 1, 0.3, 1]
          }} className="h-full">
                <Card className="h-full flex flex-col justify-between rounded-xl border border-border bg-card shadow-xs hover:shadow-md hover:border-secondary/50 transition-all duration-200 group">
                  <CardHeader className="p-5 pb-3">
                    <div className="flex items-start gap-3">
                      <div className="p-2.5 rounded-lg bg-muted text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                        <MapPin className="size-5" aria-hidden="true" />
                      </div>
                      <CardTitle className="text-base sm:text-lg font-semibold text-foreground group-hover:text-primary transition-colors duration-200 leading-snug">
                        {center.name}
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="p-5 pt-0 flex-1">
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed flex items-start gap-1.5">
                      <span className="sr-only">Địa chỉ / Address:</span>
                      {center.address}
                    </p>
                  </CardContent>

                  <CardFooter className="p-5 pt-0">
                    <Button asChild variant="outline" size="sm" className="w-full justify-between group/btn border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200">
                      <Link to={center.anchor}>
                        <span>{currentCopy.btn}</span>
                        <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" aria-hidden="true" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}